<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';

	let {
		checked = $bindable(false),
		name = undefined,
		onChange = () => void 0,
		id = undefined,
		disabled = undefined,
		size = 'md',
		dataCy,
		'aria-label': ariaLabel = undefined,
		...rest
	}: Props = $props();

	interface Props {
		checked?: boolean;
		name?: string | undefined;
		onChange?: (checked: boolean) => void;
		id?: string | undefined;
		disabled?: boolean | undefined;
		dataCy: string;
		class?: string;
		size?: 'md' | 'sm';
		'aria-label'?: string | undefined;
	}

	let getAriaChecked = $derived(() => {
		return checked;
	});

	const handleClick = (e: Event) => {
		e.preventDefault();
		if (disabled) return;

		checked = !checked;

		onChange?.(checked);
	};

	let dataCyPrefix = $derived(`${dataCy}-switch`);

	let buttonClasses = $derived(
		classNames('appearance-none rounded-full relative', {
			'bg-emerald-600': checked && disabled,
			'bg-emerald-500': checked && !disabled,
			'bg-gray-200': !checked,
			'w-[48px] h-[24px]': size === 'md',
			'w-[36px] h-[16px]': size === 'sm'
		})
	);
</script>

<button
	{id}
	onclick={handleClick}
	class={twMerge(buttonClasses, rest.class)}
	role="checkbox"
	data-cy={dataCyPrefix}
	aria-label={ariaLabel}
	aria-checked={getAriaChecked()}
	aria-disabled={disabled}
>
	<span
		class={classNames('absolute bottom-0 left-0 top-0 my-auto rounded-full bg-white transition', {
			'translate-x-[2px]': !checked,
			'translate-x-[26px]': checked && size === 'md',
			'translate-x-[22px]': checked && size === 'sm',
			'shadow-md': !disabled,
			'opacity-60': disabled,
			'h-[20px] w-[20px]': size === 'md',
			'h-[12px] w-[12px]': size === 'sm'
		})}
		data-cy={`${dataCyPrefix}-thumb`}
	></span>

	<input
		{name}
		data-cy={`${dataCyPrefix}-input`}
		type="checkbox"
		aria-hidden="true"
		hidden
		tabindex="-1"
		class="pointer-events-none absolute m-0 translate-x-[-100%] opacity-0"
	/>
</button>
